import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import {
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineFileAdd,
  AiOutlineClose,
} from "react-icons/ai";

/**
 * Stock interface matching new API structure
 */
interface Stock {
  id: number;
  name: string;
  market_cap_cr: string;
  returns_1y: string;
  returns_3y: string;
  returns_5y: string;
  [key: string]: any;
}

/**
 * Mutual Fund interface
 */
interface MutualStock {
  id: number;
  name: string;
  nav: string;
  returns_1y: string;
  returns_3y: string;
  returns_5y: string;
  [key: string]: any;
}

const API_URL = process.env.REACT_APP_API_URL;

const MINIMAL_STOCK_COLUMNS = [
  "name",
  "market_cap_cr",
  "returns_1y",
  "returns_3y",
  "returns_5y",
];

const MINIMAL_MUTUAL_STOCK_COLUMNS = [
  "name",
  "nav",
  "returns_1y",
  "returns_3y",
  "returns_5y",
];

// The full field list for minimal form value sending with "-" value for hidden fields
const FIELDS_TO_SEND = [
  "name",
  "screener",
  "ltp",
  "change_percent",
  "open_price",
  "volume",
  "market_cap_cr",
  "pe_ratio",
  "industry_pe",
  "high_52w",
  "low_52w",
  "returns_1m",
  "returns_3m",
  "returns_1y",
  "returns_3y",
  "returns_5y",
  "pb_ratio",
  "dividend",
  "roe",
  "roce",
  "eps",
  "dma_50",
  "dma_200",
  "rsi",
  "margin_funding",
  "margin_pledge",
];

const PAGE_SIZE = 20;

const StockManagement: React.FC = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [filteredStocks, setFilteredStocks] = useState<Stock[]>([]);
  const [mutualStocks, setMutualStocks] = useState<MutualStock[]>([]);
  const [filteredMutualStocks, setFilteredMutualStocks] = useState<MutualStock[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [filterField, setFilterField] = useState<string>("name");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<{ [key: string]: any }>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMutualStock, setShowMutualStock] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setCurrentPage(1);
    setSearch("");
    setFilterField("name");
    setEditIndex(null);
    setEditForm({});
    setShowAddForm(false);
    setError(null);
    if (!showMutualStock) fetchStocks();
    else fetchMutualStocks();
    // eslint-disable-next-line
  }, [showMutualStock]);

  useEffect(() => {
    const stocksData = stocks;
    const mutualData = mutualStocks;
    let filteredS = stocksData;
    let filteredM = mutualData;
    if (search.trim().length > 0) {
      const searchLc = search.trim().toLowerCase();
      filteredS = stocksData.filter((s) =>
        ((s as any)[filterField] ?? "").toString().toLowerCase().includes(searchLc)
      );
      filteredM = mutualData.filter((s) =>
        ((s as any)[filterField] ?? "").toString().toLowerCase().includes(searchLc)
      );
    }
    setFilteredStocks(filteredS);
    setFilteredMutualStocks(filteredM);
    setCurrentPage(1);
    // eslint-disable-next-line
  }, [stocks, mutualStocks, search, filterField]);

  const fetchStocks = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/auth/stocks`);
      if (!res.ok) throw new Error("Failed to fetch stocks");
      const data = await res.json();
      const massaged = Array.isArray(data)
        ? data.map((row: any) => {
            let obj: any = {};
            FIELDS_TO_SEND.forEach((col) => {
              if (
                col === "name" ||
                col === "market_cap_cr" ||
                col === "returns_1y" ||
                col === "returns_3y" ||
                col === "returns_5y"
              ) {
                obj[col] = row[col] ?? "";
              } else {
                obj[col] = "-";
              }
            });
            obj.id = row.id;
            return obj;
          })
        : [];
      setStocks(massaged);
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMutualStocks = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/auth/mutualstocks`);
      if (!res.ok) throw new Error("Failed to fetch mutual fund stocks");
      const data = await res.json();
      const massaged = Array.isArray(data)
        ? data.map((row: any) => ({
            id: row.id,
            name: row.name ?? "",
            nav: row.nav ?? "",
            returns_1y: row.returns_1y ?? "",
            returns_3y: row.returns_3y ?? "",
            returns_5y: row.returns_5y ?? ""
        }))
        : [];
      setMutualStocks(massaged);
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
      setMutualStocks([]);
    } finally {
      setLoading(false);
    }
  };

  const fillStockWithMinFields = (values: Partial<Stock>) => {
    const obj: any = {};
    FIELDS_TO_SEND.forEach((key) => {
      switch (key) {
        case "name":
        case "screener":
        case "change_percent":
        case "returns_1m":
        case "returns_3m":
        case "returns_1y":
        case "returns_3y":
        case "returns_5y":
        case "dividend":
        case "roe":
        case "roce":
        case "margin_funding":
        case "margin_pledge":
          obj[key] = values[key] ?? "";
          break;
        case "ltp":
        case "open_price":
        case "market_cap_cr":
        case "pe_ratio":
        case "industry_pe":
        case "high_52w":
        case "low_52w":
        case "pb_ratio":
        case "eps":
        case "dma_50":
        case "dma_200":
        case "rsi":
          obj[key] =
            values[key] !== undefined && values[key] !== null && values[key] !== ""
              ? values[key]
              : 0.0;
          break;
        case "volume":
          obj[key] =
            values[key] !== undefined && values[key] !== null && values[key] !== ""
              ? values[key]
              : 0;
          break;
        default:
          obj[key] = values[key] ?? null;
      }
    });
    return obj;
  };

  const addStock = async (newStock: Omit<Stock, "id">) => {
    try {
      const stockToSend = fillStockWithMinFields(newStock);
      const res = await fetch(`${API_URL}/auth/stocks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(stockToSend),
      });
      if (!res.ok) throw new Error("Failed to add stock");
      await fetchStocks();
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    }
  };

  const addMutualStock = async (newStock: Omit<MutualStock, "id">) => {
    try {
      const res = await fetch(`${API_URL}/auth/mutualstocks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newStock),
      });
      if (!res.ok) throw new Error("Failed to add mutual fund stock");
      await fetchMutualStocks();
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    }
  };

  const deleteStock = async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/auth/stocks/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete stock");
      await fetchStocks();
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    }
  };

  const deleteMutualStock = async (id: number) => {
    try {
      const res = await fetch(`${API_URL}/auth/mutualstocks/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete mutual fund stock");
      await fetchMutualStocks();
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    }
  };

  const updateStock = async (id: number, updatedStock: Partial<Stock>) => {
    try {
      const stockToSend = fillStockWithMinFields(updatedStock);
      const res = await fetch(`${API_URL}/auth/stocks/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(stockToSend),
      });
      if (!res.ok) throw new Error("Failed to update stock");
      await fetchStocks();
      setEditIndex(null);
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    }
  };

  const updateMutualStock = async (id: number, updatedStock: Partial<MutualStock>) => {
    try {
      const res = await fetch(`${API_URL}/auth/mutualstocks/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedStock),
      });
      if (!res.ok) throw new Error("Failed to update mutual fund stock");
      await fetchMutualStocks();
      setEditIndex(null);
    } catch (err: any) {
      setError(err.message ?? "Unknown error");
    }
  };

  function StockTableRow({ stock }: { stock: Stock }) {
    const isEditing = editIndex === stock.id;
    if (isEditing) {
      return (
        <tr style={{ background: "#e8ffe8" }}>
          <td colSpan={MINIMAL_STOCK_COLUMNS.length + 1} style={{ padding: "10px" }}>
            <form onSubmit={(e) => {
              e.preventDefault();
              updateStock(stock.id, editForm);
            }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {MINIMAL_STOCK_COLUMNS.map((col) => (
                  <input
                    key={col}
                    name={col}
                    placeholder={col.replace(/_/g, " ").toUpperCase()}
                    value={editForm[col] || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, [col]: e.target.value })
                    }
                    style={{ padding: 6, border: "1px solid #aaa", borderRadius: 4, minWidth: 110 }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  style={{
                    padding: "5px 16px",
                    background: "#278a3a",
                    color: "#fff",
                    borderRadius: 4,
                    border: "none"
                  }}
                >
                  <AiOutlineEdit /> Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditIndex(null); setEditForm({}) }}
                  style={{
                    padding: "5px 16px",
                    background: "#ddd",
                    color: "#222",
                    borderRadius: 4,
                    border: "none"
                  }}
                >
                  <AiOutlineClose /> Cancel
                </button>
              </div>
            </form>
          </td>
        </tr>
      );
    }
    return (
      <tr>
        {MINIMAL_STOCK_COLUMNS.map((col) => (
          <td style={{ padding: 7, borderBottom: "1px solid #eee" }} key={col}>{stock[col]}</td>
        ))}
        <td style={{ padding: 7, borderBottom: "1px solid #eee" }}>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              style={{ background: "#21836c", color: "#fff", padding: "7px 7px", borderRadius: 3, fontSize: 12, border: "none" }}
              onClick={() => {
                setEditIndex(stock.id);
                setEditForm(Object.fromEntries(MINIMAL_STOCK_COLUMNS.map(c => [c, stock[c]])));
              }}
              title="Edit"
            >
              <AiOutlineEdit /> 
            </button>
            <button
              style={{ background: "#bb3936", color: "#fff", padding: "7px 7px", borderRadius: 3, fontSize: 12, border: "none" }}
              onClick={() => deleteStock(stock.id)}
              title="Delete"
            >
              <AiOutlineDelete />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  function MutualTableRow({ stock }: { stock: MutualStock }) {
    const isEditing = editIndex === stock.id;
    if (isEditing) {
      return (
        <tr style={{ background: "#e5ffe5" }}>
          <td colSpan={MINIMAL_MUTUAL_STOCK_COLUMNS.length + 1} style={{ padding: "10px" }}>
            <form onSubmit={(e) => {
              e.preventDefault();
              updateMutualStock(stock.id, editForm);
            }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {MINIMAL_MUTUAL_STOCK_COLUMNS.map((col) => (
                  <input
                    key={col}
                    name={col}
                    placeholder={col.replace(/_/g, " ").toUpperCase()}
                    value={editForm[col] || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, [col]: e.target.value })
                    }
                    style={{ padding: 6, border: "1px solid #aaa", borderRadius: 4, minWidth: 110 }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  style={{
                    padding: "5px 16px",
                    background: "#006637",
                    color: "#fff",
                    borderRadius: 4,
                    border: "none"
                  }}
                >
                  <AiOutlineEdit /> Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditIndex(null); setEditForm({}) }}
                  style={{
                    padding: "5px 16px",
                    background: "#e5e5e5",
                    color: "#333",
                    borderRadius: 4,
                    border: "none"
                  }}
                >
                  <AiOutlineClose /> Cancel
                </button>
              </div>
            </form>
          </td>
        </tr>
      );
    }
    return (
      <tr>
        {MINIMAL_MUTUAL_STOCK_COLUMNS.map((col) => (
          <td style={{ padding: 7, borderBottom: "1px solid #eee" }} key={col}>{stock[col]}</td>
        ))}
        <td style={{ padding: 7, borderBottom: "1px solid #eee" }}>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              style={{ background: "#3f704d", color: "#fff", padding: "7px 7px", borderRadius: 3, fontSize: 12, border: "none" }}
              onClick={() => {
                setEditIndex(stock.id);
                setEditForm(Object.fromEntries(MINIMAL_MUTUAL_STOCK_COLUMNS.map(c => [c, stock[c]])));
              }}
              title="Edit"
            >
              <AiOutlineEdit /> 
            </button>
            <button
              style={{ background: "#a1433c", color: "#fff", padding: "7px 7px", borderRadius: 3, fontSize: 12, border: "none" }}
              onClick={() => deleteMutualStock(stock.id)}
              title="Delete"
            >
              <AiOutlineDelete />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  const handleExport = () => {
    const exportData = (
      showMutualStock
        ? filteredMutualStocks
        : filteredStocks
    ).map((stock) => {
      const row: any = {};
      (showMutualStock ? MINIMAL_MUTUAL_STOCK_COLUMNS : MINIMAL_STOCK_COLUMNS).forEach((col) => {
        row[col] = stock[col] ?? "";
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, showMutualStock ? "MutualStocks" : "Stocks");
    XLSX.writeFile(wb, showMutualStock ? "mutualstocks_export.xlsx" : "stocks_export.xlsx");
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target?.result;
        if (!bstr) return;
        const wb = XLSX.read(bstr, { type: "binary" });
        const sheetName = wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (showMutualStock) {
          const stocksToAdd: Omit<MutualStock, "id">[] = (rawRows as any[]).map((row) => {
            const out: any = {};
            MINIMAL_MUTUAL_STOCK_COLUMNS.forEach((col) => (out[col] = row[col] ?? ""));
            return out;
          }).filter(st => st.name);
          stocksToAdd.forEach((stock) => addMutualStock(stock));
        } else {
          const stocksToAdd: Omit<Stock, "id">[] = (rawRows as any[]).map((row) => {
            const out: any = {};
            MINIMAL_STOCK_COLUMNS.forEach((col) => (out[col] = row[col] ?? ""));
            FIELDS_TO_SEND.forEach((col) => {
              if (!MINIMAL_STOCK_COLUMNS.includes(col)) {
                out[col] = "-";
              }
            });
            return out;
          }).filter(st => st.name);
          stocksToAdd.forEach((stock) => addStock(stock));
        }
      };
      reader.readAsBinaryString(file);
      e.target.value = "";
    }
  };

  const pageCount = Math.ceil(
    (showMutualStock ? filteredMutualStocks.length : filteredStocks.length) / PAGE_SIZE
  );
  const pagedData = (showMutualStock ? filteredMutualStocks : filteredStocks).slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div style={{ padding: 18, minHeight: "80vh", borderRadius: 10, boxShadow: "0 2px 8px #e3f6e3" }}>
      <h1 style={{
        fontSize: 30, fontWeight: "bold", textAlign: "center",
        marginBottom: 28, letterSpacing: "-1px", color: "#006c3a"
      }}>
      </h1>
      <div className="flex justify-between">
      <div style={{ display: "flex", gap: 14, marginBottom: 18 }}>
        <button
          onClick={() => setShowMutualStock(false)}
          style={{
            background: showMutualStock ? "#ececec" : "#21836c",
            color: showMutualStock ? "#222" : "#fff",
            padding: "6px 19px",
            fontWeight: showMutualStock ? 500 : 700,
            border: 0,
            borderRadius: 6,
            cursor: "pointer"
          }}
        >
          Equities
        </button>
        <button
          onClick={() => setShowMutualStock(true)}
          style={{
            background: showMutualStock ? "#21836c" : "#ececec",
            color: showMutualStock ? "#fff" : "#222",
            padding: "6px 19px",
            fontWeight: showMutualStock ? 700 : 500,
            border: 0,
            borderRadius: 6,
            cursor: "pointer"
          }}
        >
          Mutual Funds
        </button>
      </div>
      <div style={{ display: "flex", gap: 17, alignItems: "center", marginBottom: 14 }}>
        <button
          onClick={handleExport}
          style={{
            background: "#21836c",
            color: "#fff",
            padding: "6px 14px",
            borderRadius: 5,
            border: "none",
            fontWeight: 500,
            display: "flex",
            gap: 6,
            alignItems: "center",
            cursor: "pointer"
          }}
        >
          <AiOutlineFileAdd /> Export to Excel
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleImport}
          style={{ display: "none" }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: "#21836c",
            color: "#fff",
            padding: "6px 14px",
            borderRadius: 5,
            border: "none",
            fontWeight: 500,
            display: "flex",
            gap: 6,
            alignItems: "center",
            cursor: "pointer"
          }}
        >
          <AiOutlineFileAdd /> Import Excel
        </button>
      </div>
      </div>
      
      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setShowAddForm((s) => !s)}
          style={{
            background: "#e6f4f1",
            color: "#247039",
            padding: "8px 38px",
            borderRadius: 8,
            fontWeight: 700,
            border: 0,
            fontSize: 16
          }}
        >
          {showAddForm
            ? (showMutualStock ? "Close Add Mutual Fund" : "Close Add Stock")
            : (showMutualStock ? "Add New Mutual Fund" : "Add New Stock")}
        </button>
      </div>
      {showAddForm && (
        showMutualStock
          ? <AddMutualStockForm onAddStock={addMutualStock} />
          : <AddStockForm onAddStock={addStock} />
      )}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: 9,
        background: "#e6f4f1",
        borderRadius: 6,
        border: "1px solid #d0edd1",
        marginBottom: 17
      }}>
        <span style={{ fontWeight: 500, color: "#116d2e" }}>Search</span>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            border: "1px solid #64c586",
            borderRadius: 4, padding: "4px 10px", minWidth: 100,
            background: "#f6fcf7"
          }}
          placeholder="Type to search..."
        />
        <span style={{ marginLeft: 9, color: "#116d2e" }}>in</span>
        <select
          value={filterField}
          onChange={e => setFilterField(e.target.value)}
          style={{
            border: "1px solid #64c586",
            borderRadius: 4,
            padding: "4px 10px",
            minWidth: 90,
            background: "#f6fcf7"
          }}
        >
          {(showMutualStock
            ? [
                { value: "name", label: "Name" },
                { value: "nav", label: "NAV" },
                { value: "returns_1y", label: "1Y %" },
                { value: "returns_3y", label: "3Y %" },
                { value: "returns_5y", label: "5Y %" }
              ]
            : [
                { value: "name", label: "Name" },
                { value: "market_cap_cr", label: "Mkt Cap (Cr)" },
                { value: "returns_1y", label: "1Y %" },
                { value: "returns_3y", label: "3Y %" },
                { value: "returns_5y", label: "5Y %" },
              ]
          ).map(opt => (
            <option value={opt.value} key={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#116d2e" }}>
          Showing <strong>
            {(showMutualStock ? filteredMutualStocks : filteredStocks).length > 0
              ? `${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(
                currentPage * PAGE_SIZE,
                (showMutualStock ? filteredMutualStocks : filteredStocks).length
              )}`
              : "0"}{" "}
            of {(showMutualStock ? filteredMutualStocks : filteredStocks).length}
          </strong>
        </span>
      </div>
      {loading && (
        <div style={{
          display: "flex", alignItems: "center", gap: 9,
          marginTop: 45, justifyContent: "center", fontSize: 17, fontWeight: 700, color: "#158a45"
        }}>
          <span style={{
            display: "inline-block",
            width: 18, height: 18, border: "2px solid #c8e3cd", borderTop: "2px solid #158a45", borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }} />
          Loading&nbsp;{showMutualStock ? "mutual funds..." : "stocks..."}
        </div>
      )}
      {error && (
        <div style={{
          background: "#ebfbea", color: "#c10400", padding: 14,
          borderRadius: 5, textAlign: "center", fontWeight: 600, margin: "12px 0"
        }}>
          {error}
        </div>
      )}
      {!loading && (showMutualStock ? filteredMutualStocks.length : filteredStocks.length) === 0 && (
        <div style={{
          color: "#439d58",
          fontSize: 16,
          textAlign: "center",
          padding: 30,
          fontWeight: 600,
          border: "1px dashed #a0dfad",
          borderRadius: 7,
          background: "#f7fff7"
        }}>
          No data found.
        </div>
      )}
      {!loading && (showMutualStock ? filteredMutualStocks : filteredStocks).length > 0 && (
        <div style={{ overflowX: "auto", background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px #cfeeca", marginTop: 6 }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 15,
            margin: "0"
          }}>
            <thead>
              <tr>
                {(showMutualStock ? MINIMAL_MUTUAL_STOCK_COLUMNS : MINIMAL_STOCK_COLUMNS).map((col) => (
                  <th key={col} style={{
                    padding: "10px 7px",
                    fontWeight: 700,
                    borderBottom: "2px solid #e6f4f1",
                    background: "#e6f4f1",
                    color: "#276445",
                    textAlign:"left"
                  }}>
                    {col.replace(/_/g, " ").toUpperCase()}
                  </th>
                ))}
                <th style={{
                  padding: "10px 7px",
                  fontWeight: 700,
                  borderBottom: "2px solid #e6f4f1",
                  background: "#e6f4f1",
                  color: "#276445",
                  textAlign:"center"
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedData.map((stock: any) =>
                showMutualStock
                  ? <MutualTableRow stock={stock} key={stock.id} />
                  : <StockTableRow stock={stock} key={stock.id} />
              )}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div style={{ display: "flex", gap: 4, justifyContent: "center", margin: "14px 0" }}>
              <button
                style={{
                  padding: "3px 7px",
                  borderRadius: "5px 0 0 5px",
                  border: "1px solid #b2e0b3",
                  color: currentPage === 1 ? "#92b894" : "#247039",
                  background: currentPage === 1 ? "#e6f4f1" : "#e6f4f1",
                  fontWeight: 500
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                &laquo;
              </button>
              <button
                style={{
                  padding: "3px 7px",
                  border: "1px solid #b2e0b3",
                  color: currentPage === 1 ? "#92b894" : "#247039",
                  background: currentPage === 1 ? "#21836c" : "#e6f4f1"
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              {Array.from({ length: pageCount }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === pageCount ||
                  Math.abs(page - currentPage) <= 2
                )
                .map((page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    return [
                      <span key={`ellipsis-${page}`} style={{ padding: "0 4px" }}>...</span>,
                      <button
                        key={page}
                        style={{
                          margin: "0 2.5px",
                          padding: "3px 7px",
                          borderRadius: 5,
                          border: "1px solid #b2e0b3",
                          background: currentPage === page ? "#21836c" : "#e6f4f1",
                          color: currentPage === page ? "#fff" : "#247039",
                          fontWeight: currentPage === page ? 700 : 500
                        }}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ];
                  }
                  return (
                    <button
                      key={page}
                      style={{
                        margin: "0 2.5px",
                        padding: "3px 7px",
                        borderRadius: 5,
                        border: "1px solid #b2e0b3",
                        background: currentPage === page ? "#21836c" : "#e6f4f1",
                        color: currentPage === page ? "#fff" : "#247039",
                        fontWeight: currentPage === page ? 700 : 500
                      }}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                style={{
                  padding: "3px 7px",
                  border: "1px solid #b2e0b3",
                  color: currentPage === pageCount ? "#92b894" : "#247039",
                  background: currentPage === pageCount ? "#21836c" : "#e6f4f1"
                }}
                disabled={currentPage === pageCount}
                onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
              >
                Next
              </button>
              <button
                style={{
                  padding: "3px 7px",
                  borderRadius: "0 5px 5px 0",
                  border: "1px solid #b2e0b3",
                  color: currentPage === pageCount ? "#92b894" : "#247039",
                  background: currentPage === pageCount ? "#21836c" : "#e6f4f1",
                  fontWeight: 500
                }}
                disabled={currentPage === pageCount}
                onClick={() => setCurrentPage(pageCount)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AddStockForm: React.FC<{
  onAddStock: (stock: Omit<Stock, "id">) => void;
}> = ({ onAddStock }) => {
  const initialForm = {
    name: "",
    market_cap_cr: "",
    returns_1y: "",
    returns_3y: "",
    returns_5y: "",
  };
  const [form, setForm] = useState<{ [key: string]: string }>(initialForm);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) return;
    onAddStock(form as any);
    setForm(initialForm);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        margin: "0 0 18px 0",
        borderRadius: 10,
        padding: 23,
        border: "1px solid #b4e6ba",
        background: "#f8fff5"
      }}
    >
      <h2 style={{
        marginBottom: 16,
        fontWeight: 700,
        fontSize: "1.22rem",
        textAlign: "center",
        color: "#217c37"
      }}>
        <AiOutlineFileAdd /> Add New Stock
      </h2>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        <div>
          <label htmlFor="name" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            Name<span style={{ color: "crimson" }}>*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form["name"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 170,
              background: "#fff"
            }}
            placeholder="Name"
            required
          />
        </div>
        <div>
          <label htmlFor="market_cap_cr" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            Market Cap (Cr)
          </label>
          <input
            id="market_cap_cr"
            name="market_cap_cr"
            type="text"
            value={form["market_cap_cr"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 120,
              background: "#fff"
            }}
            placeholder="Market Cap (Cr)"
          />
        </div>
        <div>
          <label htmlFor="returns_1y" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            1Y %
          </label>
          <input
            id="returns_1y"
            name="returns_1y"
            type="text"
            value={form["returns_1y"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 90,
              background: "#fff"
            }}
            placeholder="1Y %"
          />
        </div>
        <div>
          <label htmlFor="returns_3y" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            3Y %
          </label>
          <input
            id="returns_3y"
            name="returns_3y"
            type="text"
            value={form["returns_3y"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 90,
              background: "#fff"
            }}
            placeholder="3Y %"
          />
        </div>
        <div>
          <label htmlFor="returns_5y" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            5Y %
          </label>
          <input
            id="returns_5y"
            name="returns_5y"
            type="text"
            value={form["returns_5y"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 90,
              background: "#fff"
            }}
            placeholder="5Y %"
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 23 }}>
        <button
          type="submit"
          style={{
            padding: "7px 30px",
            background: "#16853f",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            gap: 10
          }}
        >
          <AiOutlineFileAdd /> Add Stock
        </button>
      </div>
    </form>
  );
};

const AddMutualStockForm: React.FC<{
  onAddStock: (stock: Omit<MutualStock, "id">) => void;
}> = ({ onAddStock }) => {
  const initialForm = {
    name: "",
    nav: "",
    returns_1y: "",
    returns_3y: "",
    returns_5y: "",
  };
  const [form, setForm] = useState<{ [key: string]: string }>(initialForm);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) return;
    onAddStock(form as any);
    setForm(initialForm);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        margin: "0 0 18px 0",
        borderRadius: 10,
        padding: 23,
        border: "1px solid #b4e6ba",
        background: "#f8fff5"
      }}
    >
      <h2 style={{
        marginBottom: 16,
        fontWeight: 700,
        fontSize: "1.22rem",
        textAlign: "center",
        color: "#217c37"
      }}>
        <AiOutlineFileAdd /> Add New Mutual Fund
      </h2>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        <div>
          <label htmlFor="name" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            Name<span style={{ color: "crimson" }}>*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form["name"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 170,
              background: "#fff"
            }}
            placeholder="Name"
            required
          />
        </div>
        <div>
          <label htmlFor="nav" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            NAV
          </label>
          <input
            id="nav"
            name="nav"
            type="text"
            value={form["nav"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 120,
              background: "#fff"
            }}
            placeholder="NAV"
          />
        </div>
        <div>
          <label htmlFor="returns_1y" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            1Y %
          </label>
          <input
            id="returns_1y"
            name="returns_1y"
            type="text"
            value={form["returns_1y"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 90,
              background: "#fff"
            }}
            placeholder="1Y %"
          />
        </div>
        <div>
          <label htmlFor="returns_3y" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            3Y %
          </label>
          <input
            id="returns_3y"
            name="returns_3y"
            type="text"
            value={form["returns_3y"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 90,
              background: "#fff"
            }}
            placeholder="3Y %"
          />
        </div>
        <div>
          <label htmlFor="returns_5y" style={{ display: "block", fontWeight: 600, color: "#217c37" }}>
            5Y %
          </label>
          <input
            id="returns_5y"
            name="returns_5y"
            type="text"
            value={form["returns_5y"]}
            onChange={handleChange}
            style={{
              padding: "7px",
              border: "1px solid #b4e6ba",
              borderRadius: 4,
              minWidth: 90,
              background: "#fff"
            }}
            placeholder="5Y %"
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 23 }}>
        <button
          type="submit"
          style={{
            padding: "7px 30px",
            background: "#16853f",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 15,
            display: "flex",
            alignItems: "center",
            gap: 10
          }}
        >
          <AiOutlineFileAdd /> Add Mutual Fund
        </button>
      </div>
    </form>
  );
};

export default StockManagement;